import React from "react";
import { useTranslation } from "react-i18next";
import "../states.css";

/**
 * LiveIndicator Component
 * Shows websocket connection status with visual states
 */
const STATUS = {
  CONNECTED: "connected",
  RECONNECTING: "reconnecting",
  OFFLINE: "offline",
};

const resolveStatus = ({ connectionState, isConnected }) => {
  if (connectionState === STATUS.CONNECTED) return STATUS.CONNECTED;
  if (connectionState === STATUS.RECONNECTING) return STATUS.RECONNECTING;
  if (connectionState === STATUS.OFFLINE) return STATUS.OFFLINE;
  return isConnected ? STATUS.CONNECTED : STATUS.OFFLINE;
};

const LiveIndicator = ({
  isConnected = false,
  connectionState,
  reconnectAttempt = 0,
  maxReconnectAttempts = 20,
  className = "",
}) => {
  const { t } = useTranslation("navigation");
  const status = resolveStatus({ connectionState, isConnected });
  const currentAttempt = Math.max(1, reconnectAttempt || 1);

  const statusConfig = {
    [STATUS.CONNECTED]: {
      showPing: true,
      label: t("topbar.connectionLive"),
    },
    [STATUS.RECONNECTING]: {
      showPing: false,
      label: t("topbar.connectionReconnecting", {
        current: currentAttempt,
        total: maxReconnectAttempts,
      }),
    },
    [STATUS.OFFLINE]: {
      showPing: false,
      label: t("topbar.connectionOffline"),
    },
  };

  const { showPing, label } = statusConfig[status];

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-status={status}
      className={`ui-live-indicator inline-flex items-center space-x-2 rounded-full px-3 py-1.5 ${className}`}
    >
      {/* Pulsing dot indicator */}
      <div className="relative">
        <div
          aria-hidden="true"
          className="ui-live-indicator-dot h-2 w-2 rounded-full"
        />
        {showPing && (
          <div
            aria-hidden="true"
            className="ui-live-indicator-dot ui-state-motion absolute inset-0 h-2 w-2 animate-ping rounded-full opacity-75"
          />
        )}
      </div>

      {/* Live text */}
      <span className="text-xs font-medium">{label}</span>
    </div>
  );
};

export default LiveIndicator;
